import React, { useState, useEffect } from "react";
import Parse from "parse";
import {
  Box,
  Button,
  TextField as MuiTextField,
  useTheme,
  useMediaQuery,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  Drawer,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { ViewColumn, Download, DragIndicator } from "@mui/icons-material";
import * as XLSX from "xlsx";
import {
  Datagrid,
  DateField,
  NumberField,
  TextField,
  FunctionField,
} from "react-admin";
import CustomPagination from "../../Components/CustomPagination";

// Column definitions - matching Google Sheet order
const COLUMN_DEFINITIONS = [
  { id: "spinID", label: "spinID", default: true },
  { id: "createdAt", label: "createdAt", default: false },
  { id: "tTime", label: "tTime", default: false },
  { id: "beforeBalance", label: "beforeBalance", default: true },
  { id: "txn_type", label: "txn_type", default: true },
  { id: "spinWinnings", label: "spinWinnings", default: true },
  { id: "afterBalance", label: "afterBalance", default: true },
  { id: "mismatchBeforeAfter", label: "Mismatch Before/After", default: true },
  { id: "calculation", label: "Calculation", default: true },
  { id: "spinIdValidation", label: "Spin ID Validation", default: true },
  { id: "spinType", label: "spinType", default: true },
  { id: "gameId", label: "gameId", default: false },
  { id: "gameSessionId", label: "gameSessionId", default: true },
  { id: "objectId", label: "objectId", default: false },
  { id: "userId", label: "userId", default: false },
  { id: "paylines", label: "paylines", default: false },
  { id: "amount", label: "amount", default: false },
  { id: "username", label: "username", default: false },
  { id: "gameName", label: "gameName", default: true },
];

const CustomFilter = ({ onApply, onClear, initialFilters }) => {
  // Filters - no default values
  const [username, setUsername] = useState("");
  const [gameId, setGameId] = useState("");
  const [validationStatus, setValidationStatus] = useState("");
  const [txnType, setTxnType] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [spinType, setSpinType] = useState("");

  // Fetch games
  const [games, setGames] = useState([]);

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const result = await Parse.Cloud.run("getGamesList");
        if (result && Array.isArray(result)) {
          setGames(result);
        } else {
          setGames([]);
        }
      } catch (error) {
        setGames([]);
      }
    };
    fetchGames();
  }, []);

  const handleApply = () => {
    const filters = {};
    if (username.trim()) filters.userId = username;
    if (gameId) filters.gameId = gameId;
    if (validationStatus) filters.validationStatus = validationStatus;
    if (txnType) filters.txnType = txnType;
    if (sessionId.trim()) filters.sessionId = sessionId;
    if (spinType) filters.spinType = spinType;
    onApply(filters);
  };

  const handleClear = () => {
    // Clear all filters
    setUsername("");
    setGameId("");
    setValidationStatus("");
    setTxnType("");
    setSessionId("");
    setSpinType("");
    onClear();
  };

  return (
    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
      <MuiTextField
        size="small"
        placeholder="User ID / Username"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleApply()}
        sx={{ width: 180 }}
      />

      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Game</InputLabel>
        <Select
          value={gameId}
          label="Game"
          onChange={(e) => setGameId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {games.map((game) => (
            <MenuItem key={game.id} value={game.id}>
              {game.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Validation Status</InputLabel>
        <Select
          value={validationStatus}
          label="Validation Status"
          onChange={(e) => setValidationStatus(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="Both">Both</MenuItem>
          <MenuItem value="Bet">Bet</MenuItem>
          <MenuItem value="Free Spin">Free Spin</MenuItem>
          <MenuItem value="Error">Error</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ width: 130 }}>
        <InputLabel>Transaction Type</InputLabel>
        <Select
          value={txnType}
          label="Transaction Type"
          onChange={(e) => setTxnType(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="0">Win</MenuItem>
          <MenuItem value="1">Bet</MenuItem>
        </Select>
      </FormControl>

      <MuiTextField
        size="small"
        placeholder="Session ID"
        value={sessionId}
        onChange={(e) => setSessionId(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleApply()}
        sx={{ width: 150 }}
      />

      <FormControl size="small" sx={{ width: 130 }}>
        <InputLabel>Spin Type</InputLabel>
        <Select
          value={spinType}
          label="Spin Type"
          onChange={(e) => setSpinType(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="0">Normal</MenuItem>
          <MenuItem value="1">Free</MenuItem>
          <MenuItem value="2">Bonus</MenuItem>
        </Select>
      </FormControl>

      <Button
        variant="contained"
        sx={{
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": { backgroundColor: "#FFA000" },
          minWidth: 80,
        }}
        onClick={handleApply}
      >
        Apply
      </Button>

      <Button
        variant="outlined"
        sx={{
          borderColor: "#FFC107",
          color: "white",
          textTransform: "none",
          "&:hover": { borderColor: "#FFA000" },
          minWidth: 80,
        }}
        onClick={handleClear}
      >
        Clear
      </Button>
    </Box>
  );
};

// Custom Pagination Component
const PostPagination = ({ page, perPage, total, setPage, setPerPage }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100% !important",
        margin: "16px 0px",
      }}
    >
      <CustomPagination
        page={page}
        perPage={perPage}
        total={total}
        setPage={setPage}
        setPerPage={setPerPage}
      />
    </Box>
  );
};

// No default filters - user must apply filters manually
const DEFAULT_FILTERS = {};

export const SpinValidationList = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("info");

  // Column selection and order state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(() => {
    const saved = localStorage.getItem("spinValidationColumns");
    if (saved) {
      return JSON.parse(saved);
    }
    const defaults = {};
    COLUMN_DEFINITIONS.forEach((col) => {
      defaults[col.id] = col.default;
    });
    return defaults;
  });

  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem("spinValidationColumnOrder");
    if (saved) {
      return JSON.parse(saved);
    }
    return COLUMN_DEFINITIONS.map((col) => col.id);
  });

  const [draggedItem, setDraggedItem] = useState(null);

  const fetchData = async () => {
    // Check if any filter is applied
    const hasFilters = Object.keys(filters).length > 0 && 
      Object.values(filters).some(value => value !== "" && value !== null && value !== undefined);
    
    if (!hasFilters) {
      setData([]);
      setTotal(0);
      setToastMessage("Please apply filters before fetching data");
      setToastSeverity("warning");
      setToastOpen(true);
      return;
    }
    
    setLoading(true);
    try {
      const params = {
        page,
        perPage,
        sortField: "createdAt",
        sortOrder: "DESC",
        ...filters,
      };

      const result = await Parse.Cloud.run("getSpinValidationReport", params);
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      setToastMessage(`Error: ${error.message}`);
      setToastSeverity("error");
      setToastOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, perPage, filters]);

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(1);
    setData([]);
    setTotal(0);
    setToastMessage("Please apply filters to view data");
    setToastSeverity("info");
    setToastOpen(true);
  };

  // Column selection handlers
  const handleColumnToggle = (columnId) => {
    const newSelection = {
      ...selectedColumns,
      [columnId]: !selectedColumns[columnId],
    };
    setSelectedColumns(newSelection);
    localStorage.setItem("spinValidationColumns", JSON.stringify(newSelection));
  };

  const handleSelectAll = () => {
    const allSelected = {};
    COLUMN_DEFINITIONS.forEach((col) => {
      allSelected[col.id] = true;
    });
    setSelectedColumns(allSelected);
    localStorage.setItem("spinValidationColumns", JSON.stringify(allSelected));
  };

  const handleDeselectAll = () => {
    const noneSelected = {};
    COLUMN_DEFINITIONS.forEach((col) => {
      noneSelected[col.id] = false;
    });
    setSelectedColumns(noneSelected);
    localStorage.setItem("spinValidationColumns", JSON.stringify(noneSelected));
  };

  const handleResetToDefault = () => {
    const defaults = {};
    COLUMN_DEFINITIONS.forEach((col) => {
      defaults[col.id] = col.default;
    });
    setSelectedColumns(defaults);
    localStorage.setItem("spinValidationColumns", JSON.stringify(defaults));

    const defaultOrder = COLUMN_DEFINITIONS.map((col) => col.id);
    setColumnOrder(defaultOrder);
    localStorage.setItem(
      "spinValidationColumnOrder",
      JSON.stringify(defaultOrder)
    );
  };

  // Drag and drop handlers
  const handleDragStart = (e, columnId) => {
    setDraggedItem(columnId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetColumnId) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetColumnId) return;

    const newOrder = [...columnOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(targetColumnId);

    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedItem);

    setColumnOrder(newOrder);
    localStorage.setItem("spinValidationColumnOrder", JSON.stringify(newOrder));
    setDraggedItem(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Get ordered columns
  const orderedColumns = columnOrder
    .map((id) => COLUMN_DEFINITIONS.find((col) => col.id === id))
    .filter(Boolean);

  const selectedCount = Object.values(selectedColumns).filter(Boolean).length;

  // Export to Excel
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    // Check if there's data in the table
    if (data.length === 0) {
      setToastMessage("No data available to export");
      setToastSeverity("warning");
      setToastOpen(true);
      return;
    }
    
    try {
      setExporting(true);

      // Fetch ALL data with the same filters (not just current page)
      const params = {
        page: 1,
        perPage: total, // Get all records that match the filters
        sortField: "createdAt",
        sortOrder: "DESC",
        ...filters,
      };

      const result = await Parse.Cloud.run("getSpinValidationReport", params);
      const exportData = result.data || [];

      // Export ALL columns (not just selected ones) - NO Sr. No column
      const excelData = exportData.map((row) => {
        const filteredRow = {};

        // Export all columns in the order defined in COLUMN_DEFINITIONS
        COLUMN_DEFINITIONS.forEach((col) => {
          let value = row[col.id];

          // Format values
          if (col.id === "createdAt" && value) {
            value = new Date(value).toLocaleString();
          } else if (
            [
              "beforeBalance",
              "afterBalance",
              "spinWinnings",
              "amount",
            ].includes(col.id) &&
            value !== undefined
          ) {
            value = parseFloat(value).toFixed(2);
          } else if (col.id === "txn_type" || col.id === "spinType") {
            // Keep 0 as 0, not as "-"
            value = value !== undefined && value !== null ? value : "-";
          } else if (
            col.id === "mismatchBeforeAfter" ||
            col.id === "calculation"
          ) {
            value = value ? "TRUE" : "FALSE";
          }

          filteredRow[col.label] = value !== undefined && value !== null ? value : "-";
        });

        return filteredRow;
      });

      // Create workbook and worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Spin Validation");

      // Generate filename with date and record count
      const filename = `Spin_Validation_Report_${exportData.length}_records_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;

      XLSX.writeFile(wb, filename);
      
      setToastMessage(`Export completed: ${exportData.length} records exported successfully`);
      setToastSeverity("success");
      setToastOpen(true);
    } catch (error) {
      setToastMessage(`Export Error: ${error.message}`);
      setToastSeverity("error");
      setToastOpen(true);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box
      sx={{
        mt: 2,
        backgroundColor: "#242424",
        borderRadius: 2,
        boxShadow: "none !important",
        padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
      }}
    >
      <Card
        sx={{
          backgroundColor: "#242424",
          boxShadow: "none",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={2}
          >
            <Box sx={{ flex: 1 }}>
              <CustomFilter
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
                initialFilters={DEFAULT_FILTERS}
              />
            </Box>

            <Box display="flex" flexDirection="column" gap={1}>
              <Button
                variant="contained"
                onClick={handleExport}
                disabled={exporting || data.length === 0}
                sx={{
                  background: "#4CAF50",
                  color: "white",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#45a049" },
                  "&:disabled": {
                    backgroundColor: "#666",
                    color: "#999",
                  },
                  minWidth: 130,
                }}
                startIcon={<Download />}
              >
                {exporting ? "Exporting..." : "Export Excel"}
              </Button>

              <Button
                variant="contained"
                onClick={() => setDrawerOpen(true)}
                sx={{
                  background: "#FFC107",
                  color: "#0A0A0A",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#FFB300" },
                  minWidth: 130,
                }}
                startIcon={<ViewColumn />}
              >
                Columns ({selectedCount})
              </Button>
            </Box>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <Typography color="white">Loading...</Typography>
          </Box>
        ) : data.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <Typography color="#FFC107" fontSize="16px" textAlign="center">
              Please Apply Filters before fetching the data. Session ID, User ID / Username, and Game Name are the default filters.
            </Typography>
          </Box>
        ) : (
          <Box style={{ width: "100%", overflowX: "auto" }}>
            <Datagrid
              data={data}
              bulkActionButtons={false}
              sx={{
                "& .RaDatagrid-table": {
                  backgroundColor: "#2a2a2a",
                },
                "& .RaDatagrid-headerCell": {
                  color: "#FFC107 !important",
                  fontWeight: "700",
                  fontSize: "12px",
                  backgroundColor: "#2a2a2a",
                },
                "& .RaDatagrid-rowCell": {
                  color: "white",
                  fontSize: "13px",
                  borderBottom: "1px solid #333",
                },
                "& .RaDatagrid-row:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              {orderedColumns.map((col) => {
                if (!selectedColumns[col.id]) return null;

                switch (col.id) {
                  case "spinID":
                    return (
                      <TextField key={col.id} source="spinID" label={col.label} />
                    );
                  case "createdAt":
                    return (
                      <DateField
                        key={col.id}
                        source="createdAt"
                        label={col.label}
                        showTime
                      />
                    );
                  case "tTime":
                    return (
                      <TextField key={col.id} source="tTime" label={col.label} />
                    );
                  case "beforeBalance":
                    return (
                      <NumberField
                        key={col.id}
                        source="beforeBalance"
                        label={col.label}
                        options={{
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }}
                      />
                    );
                  case "txn_type":
                    return (
                      <TextField
                        key={col.id}
                        source="txn_type"
                        label={col.label}
                      />
                    );
                  case "spinWinnings":
                    return (
                      <NumberField
                        key={col.id}
                        source="spinWinnings"
                        label={col.label}
                        options={{
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }}
                      />
                    );
                  case "afterBalance":
                    return (
                      <NumberField
                        key={col.id}
                        source="afterBalance"
                        label={col.label}
                        options={{
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }}
                      />
                    );
                  case "mismatchBeforeAfter":
                    return (
                      <FunctionField
                        key={col.id}
                        label={col.label}
                        render={(record) =>
                          record.mismatchBeforeAfter ? "TRUE" : "FALSE"
                        }
                      />
                    );
                  case "calculation":
                    return (
                      <FunctionField
                        key={col.id}
                        label={col.label}
                        render={(record) =>
                          record.calculation ? "TRUE" : "FALSE"
                        }
                      />
                    );
                  case "spinIdValidation":
                    return (
                      <FunctionField
                        key={col.id}
                        label={col.label}
                        render={(record) => {
                          const validation = record.spinIdValidation;
                          let color = "default";
                          if (validation === "Both") color = "success";
                          else if (validation === "Bet") color = "warning";
                          else if (validation === "Free Spin") color = "info";
                          else if (validation === "Error") color = "error";

                          return (
                            <Chip
                              label={validation || "-"}
                              color={color}
                              size="small"
                              variant="outlined"
                            />
                          );
                        }}
                      />
                    );
                  case "spinType":
                    return (
                      <TextField
                        key={col.id}
                        source="spinType"
                        label={col.label}
                      />
                    );
                  case "gameId":
                    return (
                      <TextField
                        key={col.id}
                        source="gameId"
                        label={col.label}
                      />
                    );
                  case "gameSessionId":
                    return (
                      <TextField
                        key={col.id}
                        source="gameSessionId"
                        label={col.label}
                      />
                    );
                  case "objectId":
                    return (
                      <TextField
                        key={col.id}
                        source="objectId"
                        label={col.label}
                      />
                    );
                  case "userId":
                    return (
                      <TextField
                        key={col.id}
                        source="userId"
                        label={col.label}
                      />
                    );
                  case "paylines":
                    return (
                      <TextField
                        key={col.id}
                        source="paylines"
                        label={col.label}
                      />
                    );
                  case "amount":
                    return (
                      <NumberField
                        key={col.id}
                        source="amount"
                        label={col.label}
                        options={{
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }}
                      />
                    );
                  case "username":
                    return (
                      <TextField
                        key={col.id}
                        source="username"
                        label={col.label}
                      />
                    );
                  case "gameName":
                    return (
                      <TextField
                        key={col.id}
                        source="gameName"
                        label={col.label}
                      />
                    );
                  default:
                    return null;
                }
              })}
            </Datagrid>
          </Box>
        )}

        <PostPagination
          page={page}
          perPage={perPage}
          total={total}
          setPage={setPage}
          setPerPage={setPerPage}
        />
      </Card>

      {/* Column Selection Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: { width: 350, p: 3, background: "#1c1c1c" },
        }}
      >
        <Typography variant="h6" color="white" sx={{ mb: 2 }}>
          Select Columns
        </Typography>

        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <Button size="small" onClick={handleSelectAll} variant="outlined">
            Select All
          </Button>
          <Button size="small" onClick={handleDeselectAll} variant="outlined">
            Deselect All
          </Button>
          <Button
            size="small"
            onClick={handleResetToDefault}
            variant="outlined"
          >
            Reset
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="body2" color="#999" sx={{ mb: 2 }}>
          Drag to reorder columns
        </Typography>

        <FormGroup>
          {orderedColumns.map((col) => (
            <Box
              key={col.id}
              draggable
              onDragStart={(e) => handleDragStart(e, col.id)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, col.id)}
              onDragEnd={handleDragEnd}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "move",
                backgroundColor:
                  draggedItem === col.id ? "#333" : "transparent",
                borderRadius: 1,
                p: 0.5,
                mb: 0.5,
                "&:hover": {
                  backgroundColor: "#2a2a2a",
                },
              }}
            >
              <DragIndicator sx={{ color: "#666", mr: 1, fontSize: 20 }} />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedColumns[col.id] || false}
                    onChange={() => handleColumnToggle(col.id)}
                    size="small"
                    sx={{
                      color: "#FFC107",
                      "&.Mui-checked": { color: "#FFC107" },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" color="white">
                    {col.label}
                    {col.default && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ ml: 1, color: "#FFC107" }}
                      >
                        (Default)
                      </Typography>
                    )}
                  </Typography>
                }
                sx={{ flex: 1, m: 0 }}
              />
            </Box>
          ))}
        </FormGroup>
      </Drawer>

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ 
          bottom: "24px !important",
          zIndex: 9999
        }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ 
            width: "100%",
            minWidth: "300px",
            fontSize: "14px",
            fontWeight: 500
          }}
          variant="filled"
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
